import { z } from 'zod';
/**
 * Level-10 Meeting — the 90-minute weekly leadership-team meeting that
 * runs the same agenda every week: Segue (good news) → Scorecard →
 * Rock Review → Customer/Employee Headlines → Todos → IDS (Identify,
 * Discuss, Solve) → Conclude (cascading messages + 1-10 rating). This
 * doc is the persisted record of one such meeting; the live meeting
 * wizard mutates it as the segments progress.
 *
 * Storage path: `l10Meetings/{meetingId}`. One doc per (orgId, weekStart).
 * Snapshots/refs to scorecard + rocks + todos + issues are stored as
 * ids — the meeting record stays small while the underlying entities
 * keep evolving in their own collections.
 */
export const L10_MEETINGS_COLLECTION = 'l10Meetings';
/**
 * Headlines — quick "what happened this week" callouts split into
 * customer-facing and employee-facing buckets so the right audience
 * pays attention.
 */
export const L10Headlines = z.object({
    customer: z.array(z.string()).default([]),
    employee: z.array(z.string()).default([]),
});
export const L10Meeting = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /**
     * ISO date string (YYYY-MM-DD) for the Monday/Sunday anchoring the
     * week — matches the org scorecard's `weekStart` convention.
     */
    weekStart: z.string(),
    /** ISO timestamp the facilitator clicked "start meeting". */
    startedAt: z.string(),
    /** ISO timestamp on conclude; absent while the meeting is in progress. */
    endedAt: z.string().optional(),
    /** Uids of everyone present at this L10. */
    attendeeUids: z.array(z.string()).default([]),
    /** 1–10 rating given at the conclude segment. EOS recommends an 8+ average. */
    rating: z.number().int().min(1).max(10).optional(),
    /** Optional pointer to a frozen Scorecard snapshot if we materialize one. */
    scorecardSnapshotId: z.string().optional(),
    /** Rocks reviewed during the rock-review segment, in display order. */
    rockReviewIds: z.array(z.string()).optional(),
    headlines: L10Headlines,
    /** Todos surfaced/closed during the meeting — refs to the `todos` collection. */
    todoIds: z.array(z.string()).default([]),
    /** Issues discussed during the IDS segment — refs to the `issues` collection. */
    issueIds: z.array(z.string()).default([]),
    /** Cascading-messages + post-meeting notes captured at conclude. */
    conclusionNotes: z.string().optional(),
    /** Uid of whoever ran the meeting — usually the Integrator. */
    facilitatorUid: z.string().min(1),
    createdAt: z.string(),
    updatedAt: z.string(),
});
